import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from '../services/api';
import { toast } from 'react-toastify';
import {
  Users,
  CreditCard,
  Zap,
  Settings,
  Shield,
  ChevronRight,
  ChevronDown,
  Search,
} from 'lucide-react';

function AdminDashboard() {
  const [tab, setTab] = useState('overview'); // overview | members | recharges | tree
  const [stats, setStats] = useState(null);
  const [members, setMembers] = useState([]);
  const [recharges, setRecharges] = useState([]);
  const [tree, setTree] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    loadStats();
  }, []);

  useEffect(() => {
    if (tab === 'members') loadMembers();
    if (tab === 'recharges') loadRecharges();
    if (tab === 'tree') loadTree();
  }, [tab]);

  const loadStats = async () => {
    try {
      const res = await api.get('/Admin/stats');
      setStats(res.data.data);
    } catch (err) {
      toast.error('Failed to load stats');
    } finally {
      setLoading(false);
    }
  };

  const loadMembers = async () => {
    try {
      const res = await api.get('/Admin/members', { params: { search } });
      setMembers(res.data.data || []);
    } catch (err) {
      toast.error('Failed to load members');
    }
  };

  const loadRecharges = async () => {
    try {
      const res = await api.get('/Admin/recharges');
      setRecharges(res.data.data || []);
    } catch (err) {
      toast.error('Failed to load recharges');
    }
  };

  const loadTree = async () => {
    try {
      const res = await api.get('/Admin/tree');
      setTree(res.data.data || []);
    } catch (err) {
      toast.error('Failed to load tree');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        Loading...
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 pb-8">
      {/* Header */}
      <div className="bg-gray-900 text-white p-4 md:p-6">
        <div className="max-w-5xl mx-auto flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Shield size={24} /> Admin Dashboard
            </h1>
            <p className="text-gray-400 text-sm">Full control panel</p>
          </div>
          <button
            onClick={() => navigate('/dashboard')}
            className="text-sm bg-white/20 px-4 py-2 rounded-lg"
          >
            ← User Dashboard
          </button>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 mt-4">
        {/* Quick Links */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
          <Link
            to="/admin/payments"
            className="bg-red-500 text-white rounded-2xl p-4 flex items-center gap-3 shadow"
          >
            <CreditCard size={22} />
            <span className="font-semibold text-sm">Payments</span>
          </Link>
          <Link
            to="/admin/settings"
            className="bg-gray-700 text-white rounded-2xl p-4 flex items-center gap-3 shadow"
          >
            <Settings size={22} />
            <span className="font-semibold text-sm">Settings</span>
          </Link>
          <Link
            to="/admin/recharge"
            className="bg-green-600 text-white rounded-2xl p-4 flex items-center gap-3 shadow"
          >
            <Zap size={22} />
            <span className="font-semibold text-sm">Recharge Setup</span>
          </Link>
          <button
            onClick={() => setTab('members')}
            className="bg-blue-600 text-white rounded-2xl p-4 flex items-center gap-3 shadow"
          >
            <Users size={22} />
            <span className="font-semibold text-sm">Members</span>
          </button>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-4 overflow-x-auto">
          {['overview', 'members', 'recharges', 'tree'].map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={`px-4 py-2 rounded-lg text-sm font-medium capitalize whitespace-nowrap ${
                tab === t ? 'bg-blue-600 text-white' : 'bg-white text-gray-700'
              }`}
            >
              {t}
            </button>
          ))}
        </div>

        {/* ===== OVERVIEW ===== */}
        {tab === 'overview' && stats && (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            <StatCard label="Total Users" value={stats.totalUsers} color="text-blue-600" />
            <StatCard label="Active Users" value={stats.activeUsers} color="text-green-600" />
            <StatCard label="Pending Payments" value={stats.pendingPayments} color="text-orange-600" />
            <StatCard label="Total Recharges" value={stats.totalRecharges} color="text-purple-600" />
            <StatCard label="Success Recharges" value={stats.successRecharges} color="text-teal-600" />
            <StatCard
              label="Total Collected"
              value={`₹${(stats.totalCollected || 0).toLocaleString()}`}
              color="text-indigo-600"
            />
          </div>
        )}

        {/* ===== MEMBERS ===== */}
        {tab === 'members' && (
          <div>
            <div className="flex gap-2 mb-4">
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search name, mobile, code..."
                className="flex-1 border rounded-xl px-4 py-2"
              />
              <button
                onClick={loadMembers}
                className="bg-blue-600 text-white px-4 py-2 rounded-xl"
              >
                <Search size={18} />
              </button>
            </div>

            <div className="space-y-3">
              {members.map((m) => (
                <div key={m.id} className="bg-white rounded-2xl p-4 shadow">
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="font-bold">{m.fullName}</p>
                      <p className="text-sm text-gray-500">{m.mobile}</p>
                      <p className="text-xs text-gray-400">
                        Code: {m.referralCode} | Sponsor: {m.sponsorName || 'None'}
                      </p>
                    </div>
                    <div className="text-right">
                      <span
                        className={`text-xs font-bold px-2 py-1 rounded-full ${
                          m.isActive
                            ? 'bg-green-100 text-green-700'
                            : 'bg-red-100 text-red-700'
                        }`}
                      >
                        {m.isActive ? 'Active' : 'Inactive'}
                      </span>
                      <p className="text-sm font-bold mt-1">₹{m.walletBalance}</p>
                      <p className="text-xs text-gray-500">{m.directMembers} directs</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ===== RECHARGES ===== */}
        {tab === 'recharges' && (
          <div className="space-y-3">
            {recharges.length === 0 ? (
              <p className="text-center text-gray-400 py-8">No recharges</p>
            ) : (
              recharges.map((r) => (
                <div key={r.id} className="bg-white rounded-2xl p-4 shadow flex justify-between">
                  <div>
                    <p className="font-bold">
                      {r.mobile || 'Membership'} {r.operatorCode && `(${r.operatorCode})`}
                    </p>
                    <p className="text-sm text-gray-500">
                      {r.userName} – {r.userMobile}
                    </p>
                    <p className="text-xs text-gray-400">
                      {new Date(r.rechargeDate).toLocaleString()}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="font-bold">₹{r.amount}</p>
                    <span
                      className={`text-xs font-bold px-2 py-0.5 rounded-full ${
                        r.status === 'Success'
                          ? 'bg-green-100 text-green-700'
                          : r.status === 'Processing'
                          ? 'bg-yellow-100 text-yellow-700'
                          : r.isFree
                          ? 'bg-blue-100 text-blue-700'
                          : 'bg-red-100 text-red-700'
                      }`}
                    >
                      {r.isFree && !r.mobile ? 'Free Plan' : r.status || 'Done'}
                    </span>
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        {/* ===== TREE VIEW ===== */}
        {tab === 'tree' && (
          <div className="bg-white rounded-2xl p-4 shadow">
            <h3 className="font-bold mb-4">Network Tree</h3>
            {tree.length === 0 ? (
              <p className="text-gray-400 text-center py-6">No data</p>
            ) : (
              tree.map((node, i) => <TreeNode key={i} node={node} level={0} />)
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function StatCard({ label, value, color }) {
  return (
    <div className="bg-white rounded-2xl p-4 shadow">
      <p className="text-xs text-gray-500">{label}</p>
      <p className={`text-2xl font-bold ${color}`}>{value}</p>
    </div>
  );
}

function TreeNode({ node, level }) {
  const [open, setOpen] = useState(level < 2);
  const hasChildren = node.children && node.children.length > 0;

  return (
    <div style={{ marginLeft: level * 16 }} className="mb-1">
      <div
        className="flex items-center gap-2 py-2 px-2 rounded-lg hover:bg-gray-50 cursor-pointer"
        onClick={() => hasChildren && setOpen(!open)}
      >
        {hasChildren ? (
          open ? <ChevronDown size={16} /> : <ChevronRight size={16} />
        ) : (
          <span className="w-4" />
        )}
        <span className="font-medium text-sm">{node.name}</span>
        <span className="text-xs text-gray-400">{node.mobile}</span>
        <span
          className={`text-xs px-1.5 py-0.5 rounded ${
            node.isActive ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'
          }`}
        >
          {node.isActive ? 'Active' : 'Inactive'}
        </span>
        {hasChildren && (
          <span className="text-xs text-gray-400">({node.children.length})</span>
        )}
      </div>
      {open &&
        hasChildren &&
        node.children.map((child, i) => (
          <TreeNode key={i} node={child} level={level + 1} />
        ))}
    </div>
  );
}

export default AdminDashboard;